import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { resendVerificationEmail } from '../../services/api';

export default function SignedUpModal({ open, onClose, email }) {
    const [status, setStatus] = useState('idle');
    const [cooldown, setCooldown] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!open) return;
        setStatus('idle');
        setCooldown(0);
        setErrorMsg('');
    }, [open]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const handleResend = async () => {
        setStatus('sending');
        setErrorMsg('');
        try {
            await resendVerificationEmail(email);
            setStatus('sent');
            setCooldown(60);
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 429) {
                if (data?.retryAfter) {
                    setCooldown(data.retryAfter);
                    setStatus('idle');
                } else {
                    setStatus('limitReached');
                }
            } else {
                setStatus('error');
                setErrorMsg(data?.message || 'Erro ao reenviar. Tente novamente.');
            }
        }
    };

    const canResend = status !== 'sending' && status !== 'limitReached' && cooldown === 0;

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>
                <Stack direction='row' alignItems='center' spacing={3}>
                    <CheckCircleIcon color='success' />
                    <Typography variant='h6'>Sua conta foi criada com sucesso!</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Typography component='p' variant='body1' sx={{ mb: 2 }}>
                    Enviamos um e-mail de confirmação para o endereço cadastrado. Clique no link do e-mail para ativar sua conta e fazer login.
                </Typography>

                {status === 'sent' && (
                    <Alert severity='success' sx={{ mb: 1 }}>
                        E-mail reenviado! Verifique sua caixa de entrada.
                    </Alert>
                )}
                {status === 'error' && (
                    <Alert severity='error' sx={{ mb: 1 }}>{errorMsg}</Alert>
                )}
                {status === 'limitReached' && (
                    <Alert severity='warning'>
                        Limite de reenvios atingido. Entre em contato com o suporte.
                    </Alert>
                )}

                {status !== 'limitReached' && (
                    <Typography variant='body2' sx={{ mt: 1 }}>
                        Não recebeu o e-mail?{' '}
                        {canResend ? (
                            <Link
                                component='button'
                                onClick={handleResend}
                                sx={{ verticalAlign: 'baseline' }}
                            >
                                {status === 'sending'
                                    ? <><CircularProgress size={12} sx={{ mr: 0.5 }} />Reenviando...</>
                                    : 'Clique aqui para reenviar'}
                            </Link>
                        ) : (
                            <Typography component='span' variant='body2' color='text.secondary'>
                                Aguarde {cooldown}s para reenviar
                            </Typography>
                        )}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Voltar para a página inicial</Button>
            </DialogActions>
        </Dialog>
    );
}
